import type { ReactNode } from 'react';
import { Heading } from '@/components/catalyst/heading';
import { Text } from '@/components/catalyst/text';

interface ErrorPageLayoutProps {
  code: string;
  label: string;
  title: string;
  description: string;
  details?: ReactNode;
  actions: ReactNode;
}

export function ErrorPageLayout({
  code,
  label,
  title,
  description,
  details,
  actions,
}: ErrorPageLayoutProps) {
  return (
    <main className="relative isolate min-h-[100dvh] overflow-hidden bg-zinc-50 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-y-0 right-0 hidden w-1/2 md:block lg:w-[58%]">
          <div
            className="absolute inset-0 bg-cover opacity-[0.10] dark:opacity-20 lg:dark:opacity-35"
            style={{
              backgroundImage: "url('/home-background.jpg')",
              backgroundPosition: '66% center',
              maskImage: 'linear-gradient(to right, transparent 0%, black 26%, black 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 26%, black 100%)',
            }}
          />
          <div className="absolute inset-0 bg-white/18 dark:bg-zinc-950/35" />
          <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-zinc-50 via-zinc-50/88 to-transparent dark:from-zinc-950 dark:via-zinc-950/85 dark:to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-zinc-50/90 via-zinc-50/60 to-transparent dark:from-zinc-950 dark:via-zinc-950/70 dark:to-transparent" />
        </div>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[100dvh] w-full max-w-7xl flex-col px-6 sm:px-8 lg:px-12">
        <header className="flex items-center py-6 sm:py-8">
          <div className="flex items-center gap-2.5">
            <img src="/logo_polo9.png" alt="Polo9" className="h-7 w-auto sm:h-8 dark:hidden" />
            <img src="/logo-white.svg" alt="" aria-hidden="true" className="hidden h-7 w-auto sm:h-8 dark:block" />
          </div>
        </header>

        <div className="grid flex-1 items-center lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <section aria-labelledby="error-page-title" className="max-w-xl py-16 lg:py-24">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 dark:text-teal-300 sm:text-sm">
              {code} · {label}
            </p>
            <Heading
              id="error-page-title"
              className="mt-5 max-w-2xl !text-4xl/10 tracking-tight sm:!text-5xl/12"
            >
              {title}
            </Heading>
            <Text className="mt-6 max-w-lg !text-base/7">{description}</Text>

            {details}

            <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-3">{actions}</div>
          </section>
          <div className="hidden lg:block" aria-hidden="true" />
        </div>

        <footer className="border-t border-zinc-200 py-5 text-xs text-zinc-500 dark:border-white/5 dark:text-zinc-500">
          Nexus · Polo9
        </footer>
      </div>
    </main>
  );
}
